import React, { useEffect, useMemo, useState } from 'react';
import {
  useTable,
  useSortBy,
  useFilters,
  Column,
  TableInstance,
  ColumnInstanceWithFilter,
} from 'react-table';
import '../../App.css';
import { getForkTypeColor } from './ForkType';
import TableBar, { FoodTableBarType } from './Bar';
import ForkIcon from '@mui/icons-material/Restaurant';
import BiomeTag from '../Tags/BiomeTag';
import CraftingStationCell from './CraftingStationCell';
import BiomeFilter from '../BiomeFilter/BiomeFilter';
import { Link } from 'react-router-dom';
import DurationTag from '../Tags/DurationTag';
import { getFilterFoodData } from '../../helperFunctions/getFilteredFoodData';
import { maxHealth, maxStamina, maxEitr, maxTotal, maxHealing, TotalFoodsInDatabase } from '../../App';
import { Food } from '../../db/foodData';
import { getImageURLRemoveSpaces, handleImageError } from '../../helperFunctions/getImageURL';
import FoodCard from './FoodCard';
import { 
  TextField, 
  InputAdornment, 
  IconButton, 
  ToggleButton, 
  ToggleButtonGroup,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Grid,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SortIcon from '@mui/icons-material/Sort';


// Define a default UI for filtering
function DefaultColumnFilter({
  column,
}: {
  column: ColumnInstanceWithFilter<Food>;
}) {
  const { filterValue, preFilteredRows, setFilter } = column;
  const count = preFilteredRows.length;

  return (
    <input
      value={filterValue || ''}
      onChange={e => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

type ViewMode = 'cards' | 'table';
type SortOption = 'name' | 'total' | 'health' | 'stamina' | 'eitr';

const FoodTable: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('total');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const allColumns: Column<Food>[] = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        show: true,
        width: 200,
        Cell: ({ cell }) => {
          return (
            <div className="food-name-cell">
              <img 
                src={getImageURLRemoveSpaces(cell.row.original.icon)} 
                alt={cell.value} 
                onError={handleImageError}
                style={{ width: '32px', height: '32px', marginRight: '8px' }} 
              />
              <Link to={`/${cell.value.toLowerCase()}`} className="food-link">{cell.value}</Link>
            </div>
          );
        }
      },
      {
        Header: 'Health',
        accessor: 'health',
        show: true,
        width: 150,
        Cell: ({ cell }) => <TableBar value={cell.value} maxValue={maxHealth} type={FoodTableBarType.Health} />
      },
      {
        Header: 'Stamina',
        accessor: 'stamina',
        show: true,
        width: 150,
        Cell: ({ cell }) => <TableBar value={cell.value} maxValue={maxStamina} type={FoodTableBarType.Stamina} />
      },
      {
        Header: 'Eitr',
        accessor: 'eitr',
        show: true,
        width: 150,
        Cell: ({ cell }) => <TableBar value={cell.value} maxValue={maxEitr} type={FoodTableBarType.Eitr} />
      },
      {
        Header: 'Total',
        accessor: 'total',
        show: true,
        width: 150,
        Cell: ({ cell }) => <TableBar value={cell.value} maxValue={maxTotal} type={FoodTableBarType.Total} />
      },
      {
        Header: 'Raw Materials',
        accessor: 'rawMaterials',
        show: true,
        width: 150,
        Cell: ({ cell }) => <span>{cell.value.join(', ')}</span>,
      },
      {
        Header: 'Fork',
        accessor: 'forkType',
        show: true,
        width: 40,
        Cell: ({ cell }) => <div className='center'><ForkIcon sx={{ color: getForkTypeColor(cell.value) }} /></div>
      },
      {
        Header: 'Healing',
        accessor: 'healing',
        show: true,
        width: 150,
        Cell: ({ cell }) => <TableBar value={cell.value} maxValue={maxHealing} type={FoodTableBarType.Healing} />
      },
      {
        Header: 'Duration',
        accessor: 'duration',
        show: true,
        width: 150,
        Cell: ({ cell }) => <DurationTag duration={cell.value} />
      },
      {
        Header: 'Weight',
        accessor: 'weight',
        show: true,
        width: 150,
        Cell: ({ cell }) => <span className="number-cell">{cell.value}</span>,
      },
      {
        Header: 'Stack',
        accessor: 'stackSize',
        show: true,
        width: 50,
        Cell: ({ cell }) => <span className="number-cell">{cell.value}</span>,
      },
      {
        Header: 'Biome',
        accessor: 'biomeProgression',
        show: true,
        width: 150,
        Cell: ({ cell }) => <BiomeTag biome={cell.value} link /> 
      },
      {
        Header: 'Crafting Station',
        accessor: 'craftingStation',
        show: true,
        width: 150,
        Cell: ({ cell }) => (
          <CraftingStationCell 
            cell={{ 
              craftingStation: cell.row.original.craftingStation, 
              craftingStationLevel: cell.row.original.craftingStationLevel 
            }} 
          />
        ),
      },
      {
        Header: 'Crafting Multiplier',
        accessor: 'craftingMultiplier',
        show: true,
        width: 150,
        Cell: ({ cell }) => cell.value > 0 ? <span className="number-cell">{cell.value}</span> : <div></div>,
      },
    ],
    []
  );


  const [reload, setReload] = useState<boolean>(false);
  const [data, setData] = useState<Food[]>(() => { return getFilterFoodData() });

  useEffect(() => {
    setData(getFilterFoodData());
  }, [reload]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(food => 
      food.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort data
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'total':
          comparison = a.total - b.total;
          break;
        case 'health':
          comparison = a.health - b.health;
          break;
        case 'stamina':
          comparison = a.stamina - b.stamina;
          break;
        case 'eitr':
          comparison = a.eitr - b.eitr;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [data, searchQuery, sortBy, sortOrder]);

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const visibleColumns = useMemo(
    () => allColumns.filter(column => column.show),
    [allColumns]
  );


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns: visibleColumns,
      data: filteredAndSortedData,
      defaultColumn,
    },
    useFilters,
    useSortBy
  ) as TableInstance<Food>;

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: ViewMode | null,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleSortChange = (event: any) => {
    setSortBy(event.target.value as SortOption);
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="food-table">
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between'
        }}>
          {/* Search Bar */}
          <TextField
            placeholder="Search foods..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              flex: 1,
              maxWidth: { md: '400px' },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--secondary-bg-color)',
                color: 'var(--text-color)',
                '& fieldset': {
                  borderColor: 'var(--highlight-color)',
                },
                '&:hover fieldset': {
                  borderColor: '#f0db4f',
                },
              },
              '& .MuiInputBase-input': {
                color: 'var(--text-color)',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#999',
                opacity: 1,
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
            size="small"
          />

          {/* View Mode and Sort Controls */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            justifyContent: { xs: 'space-between', md: 'flex-start' }
          }}>
            {/* Sort Controls */}
            <FormControl size="small" sx={{ minWidth: 120, flex: { xs: '1 1 auto', md: '0 0 auto' } }}>
              <InputLabel sx={{ color: '#999', '&.Mui-focused': { color: '#f0db4f' } }}>
                Sort By
              </InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={handleSortChange}
                sx={{
                  backgroundColor: 'var(--secondary-bg-color)',
                  color: 'var(--text-color)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--highlight-color)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f0db4f',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'var(--text-color)',
                  }
                }}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="total">Total Stats</MenuItem>
                <MenuItem value="health">Health</MenuItem>
                <MenuItem value="stamina">Stamina</MenuItem>
                <MenuItem value="eitr">Eitr</MenuItem>
              </Select>
            </FormControl>

            <IconButton 
              onClick={toggleSortOrder}
              sx={{ 
                backgroundColor: 'var(--secondary-bg-color)',
                color: 'var(--text-color)',
                border: '1px solid var(--highlight-color)',
                '&:hover': {
                  backgroundColor: 'var(--tertiary-bg-color)',
                  borderColor: '#f0db4f',
                }
              }}
            >
              <SortIcon sx={{ 
                transform: sortOrder === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: 'transform 0.3s'
              }} />
            </IconButton>

            {/* View Mode Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: 'var(--text-color)',
                  borderColor: 'var(--highlight-color)',
                  '&.Mui-selected': {
                    backgroundColor: '#f0db4f',
                    color: 'black',
                    '&:hover': {
                      backgroundColor: '#d4c044',
                    }
                  },
                  '&:hover': {
                    backgroundColor: 'var(--tertiary-bg-color)',
                  }
                }
              }}
            >
              <ToggleButton value="cards">
                <ViewModuleIcon />
              </ToggleButton>
              <ToggleButton value="table">
                <ViewListIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Stats and Filters Row */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between'
        }}>
          <Typography sx={{ color: 'var(--text-color)', fontSize: '14px' }}>
            {filteredAndSortedData.length} foods found
            {searchQuery && ` (filtered by search)`}
            {getFilterFoodData().length !== TotalFoodsInDatabase && 
              `, ${TotalFoodsInDatabase - getFilterFoodData().length} filtered out by biome`}
          </Typography>
          <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
            <BiomeFilter onBiomesChange={() => setReload(!reload)} />
          </Box>
        </Box>
      </Box>

      {/* Content Section */}
      {viewMode === 'cards' ? (
        <Grid container spacing={6}>
          {filteredAndSortedData.map((food) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={food.name}>
              <FoodCard food={food} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ overflowX: 'auto', width: '100%' }}>
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())} style={{ width: column.width }}>
                      {column.render('Header')}
                      {column.isSorted
                        ? column.isSortedDesc
                          ? ' 🔽'
                          : ' 🔼'
                        : ''}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}

      {filteredAndSortedData.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          color: '#999'
        }}>
          <Typography variant="h6">No foods found</Typography>
          <Typography variant="body2">Try adjusting your search or filters</Typography>
        </Box>
      )}
    </div>
  );
};

export default FoodTable;
